import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import mermaid from "mermaid";
import { io } from "socket.io-client";
import { edges, filterModes, nodeRoutes, roleOrder, roles, workflowNodes } from "./flowConfig";

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 50;
const INITIAL_ZOOM = MAX_ZOOM;
const COMMENTS_STORAGE_KEY = "aacrm_workflow_comments";
const COMMENTS_SHORTCUT_KEY = "c";
const COMMENT_OFFSET_X = 24;
const COMMENT_OFFSET_Y = 20;
const COMMENTS_API_BASE = (process.env.REACT_APP_WORKFLOW_API_URL || "http://localhost:4000").replace(/\/$/, "");

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const nodeTooltips = {
  "Create Job Opening": "Create and publish a role requirement for new hiring.",
  "Manage Job Openings": "Track and manage active and closed openings.",
  "Hiring": "Move shortlisted candidates into confirmed hiring.",
  "Create Employee": "Stores employee data and documents.",
  "Manage Employees": "Stores employee data and documents.",
  "Upload Documents": "Stores employee data and documents.",
  "Attendance Management": "Tracks office and field attendance.",
  "Office Attendance Punch Machine": "Tracks office and field attendance.",
  "Field Attendance Mobile Selfie GPS": "Tracks office and field attendance.",
  "Leave Management": "Review and approve employee leave records.",
  "Payroll Generation": "Monthly salary processing.",
  "Login": "Authenticate and open the daily workflow session.",
  "Selfie Attendance": "Mark attendance and verify daily field presence.",
  "Create Daily Task": "Plan today tasks and define operational coverage.",
  "Submit Task to HPAM": "Send planned task scope for HPAM review.",
  "Review Daily Task": "Validate daily coverage scope before field execution.",
  "Task Approved": "Decision gate that unlocks or loops task planning.",
  "Area selection": "Select city, zone, and street for today's work.",
  "Street mapping": "Capture street-level details and store insights.",
  "Survey": "Analyze market and existing stores.",
  "Property identification": "Find available commercial properties.",
  "Developer connection": "Contact property owners and developers.",
  "Data upload": "Upload property details, images, and documents.",
  "Report submission": "Submit daily work report and findings.",
  "Review data": "Review submitted PAM data for validation quality.",
  "Data valid": "Decision point for validation pass or rework.",
  "Review": "Review records before admin progression.",
  "Modify": "Update records based on compliance or quality gaps.",
  "Modify required": "Decision point to modify or push to CRM.",
  "Approve and push CRM": "Approve finalized records and push to CRM.",
  "Store inventory": "Store approved property in CRM inventory.",
  "Manage data": "Maintain and update property information.",
  "Trigger presentation": "Initiate presentation package generation.",
  "Create PPT": "Build presentation for business and brand teams.",
  "Upload presentation": "Upload finalized presentation assets.",
  "Send to LH and BDM": "Share presentation with leasing and business teams.",
  "Connect with brands": "Initiate outreach to partner brands.",
  "Share property options": "Share curated property options for consideration.",
  "Interested": "Decision point for brand interest qualification.",
  "Drop lead": "Close non-converting lead path.",
  "Brand Requirement": "Capture brand requirement details before any property sharing.",
  "Create Mandate": "Define brand requirement like location, budget, size.",
  "Review Options": "Review matched property options returned from CRM.",
  "Shortlist": "Shortlist preferred properties after review.",
  "Match Properties": "System matches properties based on mandate.",
  "Request visit": "Request physical site visit before decision.",
  "Property selected": "Decision point for rejection or confirmation.",
  "Confirm property": "Confirm selected property for deal closure.",
  "Schedule visit": "Plan visit schedule with operations team.",
  "Assign PAM": "Assign PAM for on-ground site walkthrough.",
  "Conduct site visit": "Execute field visit and gather observations.",
  "Draft LOI": "Draft letter of intent for commercial terms.",
  "Negotiate terms": "Negotiate commercial and legal conditions.",
  "Create agreement": "Create final agreement draft.",
  "Finalize documents": "Finalize signed legal documentation.",
  "Generate invoice": "Generate invoice based on finalized terms.",
  "Send invoice": "Send invoice to brand finance contact.",
  "Payment done": "Decision point for payment completion status.",
  "Follow up": "Follow up on pending payment.",
  "Confirm payment": "Confirm received payment and close finance step.",
  "Setup": "Prepare store setup execution plan.",
  "Infrastructure setup": "Complete infrastructure readiness activities.",
  "Final inspection": "Perform final pre-launch quality inspection.",
  "Launch store": "Launch store and close workflow lifecycle.",
};

const normalizeLabel = (value = "") => value.replace(/\s+/g, " ").trim();

const WorkflowDashboard = () => {
  const navigate = useNavigate();
  const mermaidContainerRef = useRef(null);
  const frameRef = useRef(null);
  const velocityRef = useRef({ vx: 0, vy: 0 });
  const dragRef = useRef({ active: false, x: 0, y: 0, lastX: 0, lastY: 0 });
  const socketRef = useRef(null);

  const [filterMode, setFilterMode] = useState("full");
  const [zoom, setZoom] = useState(INITIAL_ZOOM);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [activeRole, setActiveRole] = useState("");
  const [hoverRole, setHoverRole] = useState("");
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, title: "", detail: "" });
  const [connectionState, setConnectionState] = useState("disconnected");
  const [liveProgress, setLiveProgress] = useState({});
  const [showSummary, setShowSummary] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [hoverStep, setHoverStep] = useState("");
  const [comments, setComments] = useState([]);
  const [commentsHydrated, setCommentsHydrated] = useState(false);
  const [commentDraft, setCommentDraft] = useState({ author: "", message: "" });
  const [cursorPoint, setCursorPoint] = useState({ x: 48, y: 48 });

  const selectedGroups = filterModes[filterMode].groups;

  const filteredEdges = useMemo(
    () => edges.filter((edge) => selectedGroups.includes(edge.group)),
    [selectedGroups]
  );

  const visibleNodeIds = useMemo(() => {
    const ids = new Set();
    filteredEdges.forEach(({ from, to }) => {
      ids.add(from);
      ids.add(to);
    });
    return ids;
  }, [filteredEdges]);

  const visibleRoleIds = useMemo(() => {
    const ids = new Set();
    workflowNodes.forEach((node) => {
      if (visibleNodeIds.has(node.id)) {
        ids.add(node.roleId);
      }
    });
    return ids;
  }, [visibleNodeIds]);

  const nodeMap = useMemo(() => {
    const mapped = {};
    workflowNodes.forEach((node) => {
      mapped[node.id] = node;
    });
    return mapped;
  }, []);

  const roleNodes = useMemo(() => {
    const grouped = {};
    roleOrder.forEach((roleId) => {
      grouped[roleId] = [];
    });

    workflowNodes.forEach((node) => {
      if (visibleNodeIds.has(node.id) && grouped[node.roleId]) {
        grouped[node.roleId].push(node);
      }
    });

    return grouped;
  }, [visibleNodeIds]);

  const nodeLabelIndex = useMemo(() => {
    const index = {};
    workflowNodes.forEach((node) => {
      index[node.label.toLowerCase()] = node;
    });
    return index;
  }, []);

  const mermaidDefinition = useMemo(() => {
    const lines = ["flowchart LR"];

    roleOrder.forEach((roleId) => {
      const role = roles[roleId];
      const nodes = roleNodes[roleId] || [];
      if (!role || nodes.length === 0) {
        return;
      }

      lines.push(`subgraph ${roleId}_LANE["${role.label}"]`);
      lines.push("direction TB");
      nodes.forEach((node) => {
        lines.push(`${node.id}["${node.label}"]`);
      });
      lines.push("end");
    });

    filteredEdges.forEach((edge) => {
      lines.push(`${edge.from} -->|${edge.label}| ${edge.to}`);
    });

    roleOrder.forEach((roleId) => {
      const role = roles[roleId];
      if (!role || !visibleRoleIds.has(roleId)) {
        return;
      }

      const className = `role_${roleId}`;
      lines.push(`classDef ${className} fill:${role.color},stroke:#e2e8f0,stroke-width:1.5px,color:#ffffff`);
      (roleNodes[roleId] || []).forEach((node) => {
        lines.push(`class ${node.id} ${className}`);
      });
    });

    lines.push("classDef decision fill:#0f172a,stroke:#38bdf8,stroke-width:2px,color:#bae6fd");
    lines.push("classDef taskNode fill:#0b253f,stroke:#60a5fa,stroke-width:2px,color:#dbeafe");
    lines.push("class PAM_TASK_CREATE taskNode");
    lines.push("class PAM_TASK_SUBMIT taskNode");
    lines.push("class HPAM_TASK_REVIEW taskNode");
    lines.push("class HPAM_TASK_DECISION decision");
    lines.push("class HPAM_VALID decision");
    lines.push("class AD_DECIDE decision");
    lines.push("class BDM_INTEREST decision");
    lines.push("class BRAND_SELECT decision");
    lines.push("class FIN_PAID decision");

    return lines.join("\n");
  }, [filteredEdges, roleNodes, visibleRoleIds]);

  const resolveNodeFromText = useCallback(
    (text = "") => {
      const normalized = text.toLowerCase().replace(/\s+/g, " ").trim();
      if (!normalized) {
        return null;
      }

      if (nodeLabelIndex[normalized]) {
        return nodeLabelIndex[normalized];
      }

      const entries = Object.entries(nodeLabelIndex).sort((a, b) => b[0].length - a[0].length);
      for (const [label, node] of entries) {
        if (normalized.includes(label)) {
          return node;
        }
      }

      return null;
    },
    [nodeLabelIndex]
  );

  const resolveNodeFromElement = useCallback(
    (element) => {
      if (!element) {
        return null;
      }

      const metadata = `${element.getAttribute("id") || ""} ${element.getAttribute("class") || ""}`;
      const mapped = Object.values(nodeMap).find((node) => metadata.includes(node.id));
      if (mapped) {
        return mapped;
      }

      return resolveNodeFromText(element.textContent || "");
    },
    [nodeMap, resolveNodeFromText]
  );

  const setGraphHoverState = useCallback(
    (nodeId, isHovering) => {
      if (!mermaidContainerRef.current) {
        return;
      }

      const container = mermaidContainerRef.current;
      const nodeGroups = container.querySelectorAll("g.node");
      const edgeGroups = container.querySelectorAll("g.edgePath");

      const connectedNodes = new Set([nodeId]);
      filteredEdges.forEach((edge) => {
        if (edge.from === nodeId || edge.to === nodeId) {
          connectedNodes.add(edge.from);
          connectedNodes.add(edge.to);
        }
      });

      nodeGroups.forEach((node) => {
        const resolved = resolveNodeFromElement(node);
        if (!isHovering) {
          node.style.opacity = "1";
          node.style.filter = "none";
          return;
        }

        const isConnected = resolved && connectedNodes.has(resolved.id);
        node.style.opacity = isConnected ? "1" : "0.22";
        node.style.filter = isConnected ? "drop-shadow(0 0 8px rgba(56, 189, 248, 0.75))" : "none";
      });

      edgeGroups.forEach((edge) => {
        const edgeId = edge.getAttribute("id") || "";
        if (!isHovering) {
          edge.style.opacity = "1";
          edge.style.filter = "none";
          return;
        }

        const highlighted = edgeId.includes(`-${nodeId}-`);
        edge.style.opacity = highlighted ? "1" : "0.18";
        edge.style.filter = highlighted ? "drop-shadow(0 0 8px rgba(14, 165, 233, 0.9))" : "none";
      });
    },
    [filteredEdges, resolveNodeFromElement]
  );

  const simulateWorkflowTransition = useCallback(async (roleId) => {
    const role = roles[roleId];
    if (!role) {
      return;
    }

    setLiveProgress((prev) => ({
      ...prev,
      [roleId]: { status: "in_progress", endpoint: role.apiEndpoint, at: new Date().toISOString() },
    }));

    socketRef.current?.emit("workflow:transition:start", {
      roleId,
      endpoint: role.apiEndpoint,
      timestamp: Date.now(),
    });

    await new Promise((resolve) => setTimeout(resolve, 550));

    setLiveProgress((prev) => ({
      ...prev,
      [roleId]: { status: "completed", endpoint: role.apiEndpoint, at: new Date().toISOString() },
    }));

    socketRef.current?.emit("workflow:transition:completed", {
      roleId,
      endpoint: role.apiEndpoint,
      timestamp: Date.now(),
    });
  }, []);

  const handleTaskCreate = useCallback(() => {
    console.log("Task Created");
  }, []);

  const handleTaskApproval = useCallback(() => {
    console.log("Task Approved");
  }, []);

  const onNodeClick = useCallback(
    async (node) => {
      if (!node) {
        return;
      }

      if (node.id === "PAM_TASK_CREATE") {
        handleTaskCreate();
      }

      if (node.id === "HPAM_TASK_DECISION") {
        handleTaskApproval();
      }

      const roleId = node.roleId;

      setActiveRole(roleId);
      await simulateWorkflowTransition(roleId);

      const fallbackRoute = roles[roleId]?.route;
      const route = nodeRoutes[roleId] || fallbackRoute;
      if (route) {
        navigate(route);
      }
    },
    [handleTaskApproval, handleTaskCreate, navigate, simulateWorkflowTransition]
  );

  const stopInertia = useCallback(() => {
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadComments = async () => {
      try {
        const response = await fetch(`${COMMENTS_API_BASE}/api/comments`);
        if (response.ok) {
          const payload = await response.json();
          const apiComments = Array.isArray(payload?.comments) ? payload.comments : [];
          if (mounted) {
            setComments(apiComments);
          }
          return;
        }
      } catch (error) {
        console.warn("Comments API unavailable, using local storage fallback.", error);
      }

      try {
        const saved = localStorage.getItem(COMMENTS_STORAGE_KEY);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed)) {
            if (mounted) {
              setComments(parsed);
            }
          } else if (Array.isArray(parsed?.comments) && mounted) {
            setComments(parsed.comments);
          }
        }
      } catch (error) {
        console.error("Unable to load comments:", error);
      } finally {
        if (mounted) {
          setCommentsHydrated(true);
        }
      }
    };

    loadComments();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!commentsHydrated) {
      return;
    }

    try {
      localStorage.setItem(COMMENTS_STORAGE_KEY, JSON.stringify(comments));
    } catch (error) {
      console.error("Unable to save comments:", error);
    }
  }, [comments, commentsHydrated]);

  useEffect(() => {
    const onKeyDown = (event) => {
      const target = event.target;
      const tag = target?.tagName?.toLowerCase();
      const isTypingField =
        tag === "input" || tag === "textarea" || target?.isContentEditable || target?.closest("[contenteditable='true']");

      if (isTypingField) {
        return;
      }

      if (event.key?.toLowerCase() === COMMENTS_SHORTCUT_KEY) {
        event.preventDefault();
        setShowComments(true);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const addComment = useCallback(async () => {
    const author = commentDraft.author.trim();
    const message = commentDraft.message.trim();
    const resolvedStep = hoverStep || "";
    if (!message || !resolvedStep) {
      return;
    }

    const localComment = {
      id: crypto.randomUUID(),
      author: author || "Client",
      step: resolvedStep,
      message,
      position: {
        x: cursorPoint.x,
        y: cursorPoint.y,
      },
      positionMode: "diagram",
      createdAt: new Date().toISOString(),
    };

    let commentToStore = localComment;

    try {
      const response = await fetch(`${COMMENTS_API_BASE}/api/comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(localComment),
      });

      if (response.ok) {
        const payload = await response.json();
        if (payload?.comment) {
          commentToStore = payload.comment;
        }
      }
    } catch (error) {
      console.warn("Comments API unavailable, comment stored locally.", error);
    }

    setComments((prev) => [
      commentToStore,
      ...prev,
    ]);

    setCommentDraft((prev) => ({ ...prev, message: "" }));
  }, [commentDraft, cursorPoint.x, cursorPoint.y, hoverStep]);

  useEffect(() => {
    mermaid.initialize({
      startOnLoad: false,
      theme: "dark",
      securityLevel: "loose",
      flowchart: {
        curve: "basis",
        nodeSpacing: 40,
        rankSpacing: 85,
        htmlLabels: true,
      },
    });
  }, []);

  useEffect(() => {
    const socketUrl = process.env.REACT_APP_WORKFLOW_SOCKET_URL || "http://localhost:4000";
    const socket = io(socketUrl, {
      transports: ["websocket"],
      reconnection: true,
      timeout: 2000,
    });

    socketRef.current = socket;

    socket.on("connect", () => setConnectionState("connected"));
    socket.on("disconnect", () => setConnectionState("disconnected"));
    socket.on("connect_error", () => setConnectionState("offline"));
    socket.on("workflow:progress", (payload) => {
      if (!payload?.roleId) {
        return;
      }

      setLiveProgress((prev) => ({
        ...prev,
        [payload.roleId]: {
          status: payload.status || "in_progress",
          endpoint: payload.endpoint || roles[payload.roleId]?.apiEndpoint,
          at: new Date().toISOString(),
        },
      }));
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  useEffect(() => {
    let canceled = false;
    const listeners = [];
    const container = mermaidContainerRef.current;

    const renderDiagram = async () => {
      try {
        const id = `workflow-${crypto.randomUUID()}`;
        const { svg } = await mermaid.render(id, mermaidDefinition);
        if (canceled || !container) {
          return;
        }

        container.innerHTML = svg;
        const nodes = container.querySelectorAll("g.node");

        nodes.forEach((node) => {
          node.style.cursor = "pointer";

          const clickHandler = () => {
            const resolvedNode = resolveNodeFromElement(node);
            onNodeClick(resolvedNode);
          };

          const enterHandler = (event) => {
            const resolvedNode = resolveNodeFromElement(node);
            const roleId = resolvedNode?.roleId;
            if (!resolvedNode || !roleId || !roles[roleId]) {
              return;
            }

            const label = normalizeLabel(resolvedNode.label || node.innerText || "");
            const detail = nodeTooltips[label] || `Step: ${label}`;
            node.setAttribute("title", detail);
            setHoverStep(label);

            setHoverRole(roleId);
            setTooltip({
              visible: true,
              x: event.clientX + 12,
              y: event.clientY + 12,
              title: `${roles[roleId].label} • ${label}`,
              detail,
            });
            setGraphHoverState(resolvedNode.id, true);
          };

          const moveHandler = (event) => {
            setTooltip((prev) => ({ ...prev, x: event.clientX + 12, y: event.clientY + 12 }));
          };

          const leaveHandler = () => {
            setHoverRole("");
            setTooltip((prev) => ({ ...prev, visible: false }));
            setGraphHoverState("", false);
          };

          node.addEventListener("click", clickHandler);
          node.addEventListener("mouseenter", enterHandler);
          node.addEventListener("mousemove", moveHandler);
          node.addEventListener("mouseleave", leaveHandler);

          listeners.push({ node, clickHandler, enterHandler, moveHandler, leaveHandler });
        });
      } catch (error) {
        if (!canceled) {
          console.error("Mermaid render error:", error);
        }
      }
    };

    renderDiagram();

    return () => {
      canceled = true;
      listeners.forEach(({ node, clickHandler, enterHandler, moveHandler, leaveHandler }) => {
        node.removeEventListener("click", clickHandler);
        node.removeEventListener("mouseenter", enterHandler);
        node.removeEventListener("mousemove", moveHandler);
        node.removeEventListener("mouseleave", leaveHandler);
      });

      if (container) {
        container.innerHTML = "";
      }
    };
  }, [mermaidDefinition, onNodeClick, resolveNodeFromElement, setGraphHoverState]);

  useEffect(() => {
    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, []);

  const zoomTo = useCallback((nextZoom) => {
    setZoom((current) => clamp(nextZoom ?? current, MIN_ZOOM, MAX_ZOOM));
  }, []);

  const zoomIn = useCallback(() => zoomTo(zoom + 0.2), [zoom, zoomTo]);
  const zoomOut = useCallback(() => zoomTo(zoom - 0.2), [zoom, zoomTo]);

  const onWheel = useCallback(
    (event) => {
      event.preventDefault();
      const delta = event.deltaY > 0 ? -0.08 : 0.08;
      const next = clamp(zoom + delta, MIN_ZOOM, MAX_ZOOM);
      setZoom(next);
    },
    [zoom]
  );

  const startInertia = useCallback(() => {
    stopInertia();

    const loop = () => {
      const { vx, vy } = velocityRef.current;
      if (Math.abs(vx) < 0.2 && Math.abs(vy) < 0.2) {
        frameRef.current = null;
        return;
      }

      setPan((prev) => ({ x: prev.x + vx, y: prev.y + vy }));
      velocityRef.current = { vx: vx * 0.92, vy: vy * 0.92 };
      frameRef.current = requestAnimationFrame(loop);
    };

    frameRef.current = requestAnimationFrame(loop);
  }, [stopInertia]);

  const onPointerDown = useCallback(
    (event) => {
      stopInertia();
      dragRef.current = {
        active: true,
        x: event.clientX - pan.x,
        y: event.clientY - pan.y,
        lastX: event.clientX,
        lastY: event.clientY,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [pan.x, pan.y, stopInertia]
  );

  const onPointerMove = useCallback((event) => {
    const canvasRect = event.currentTarget.getBoundingClientRect();
    setCursorPoint({
      x: (event.clientX - canvasRect.left - pan.x) / zoom,
      y: (event.clientY - canvasRect.top - pan.y) / zoom,
    });

    if (!dragRef.current.active) {
      return;
    }

    const nextX = event.clientX - dragRef.current.x;
    const nextY = event.clientY - dragRef.current.y;

    velocityRef.current = {
      vx: event.clientX - dragRef.current.lastX,
      vy: event.clientY - dragRef.current.lastY,
    };

    dragRef.current.lastX = event.clientX;
    dragRef.current.lastY = event.clientY;
    setPan({ x: nextX, y: nextY });
  }, [pan.x, pan.y, zoom]);

  const onPointerUp = useCallback((event) => {
    if (!dragRef.current.active) {
      return;
    }

    dragRef.current.active = false;
    event.currentTarget.releasePointerCapture(event.pointerId);
    startInertia();
  }, [startInertia]);

  return (
    <div style={styles.page}>
      <div style={styles.backdropOrbOne} />
      <div style={styles.backdropOrbTwo} />
      <div style={styles.backdropNoise} />
      <header style={styles.header}>
        <div>
          <h1 style={styles.title}>AA Workflow Dashboard</h1>
          <p style={styles.subtitle}>AA CRM Swimlane Workflow</p>
        </div>
        <div style={styles.headerRight}>
          <button style={styles.summaryBtn} onClick={() => setShowSummary(true)}>
            Flow Summary
          </button>
          <button style={styles.commentsBtn} onClick={() => setShowComments(true)}>
            Comments (C)
          </button>
          <span style={styles.connection(connectionState)}>{connectionState.toUpperCase()}</span>
          <button style={styles.btn} onClick={zoomIn}>+</button>
          <button style={styles.btn} onClick={zoomOut}>-</button>
        </div>
      </header>

      <div style={styles.layout}>
        <main style={styles.canvasWrap}>
          <div
            style={styles.canvas}
            onWheel={onWheel}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            <div
              style={{
                ...styles.diagram,
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              }}
            >
              <div ref={mermaidContainerRef} />
            </div>
          </div>
          {comments.map((comment) => (
            <div
              key={comment.id}
              style={{
                ...styles.diagramCommentPinned,
                left:
                  comment.positionMode === "diagram"
                    ? (comment.position?.x ?? 20) * zoom + pan.x + COMMENT_OFFSET_X
                    : (comment.position?.x ?? 20) + COMMENT_OFFSET_X,
                top:
                  comment.positionMode === "diagram"
                    ? (comment.position?.y ?? 20) * zoom + pan.y + COMMENT_OFFSET_Y
                    : (comment.position?.y ?? 20) + COMMENT_OFFSET_Y,
              }}
            >
              <div style={styles.diagramCommentMeta}>
                <strong>{comment.author}</strong>
                {comment.step && <span>{comment.step}</span>}
              </div>
              <div>{comment.message}</div>
            </div>
          ))}
          {comments.length === 0 && <div style={styles.diagramCommentsEmpty}>No client comments yet.</div>}
        </main>
      </div>

      {tooltip.visible && (
        <div style={{ ...styles.tooltip, left: tooltip.x, top: tooltip.y }}>
          <strong>{tooltip.title}</strong>
          <div>{tooltip.detail}</div>
        </div>
      )}

      {showSummary && (
        <div style={styles.summaryOverlay} onClick={() => setShowSummary(false)}>
          <div style={styles.summaryModal} onClick={(event) => event.stopPropagation()}>
            <div style={styles.summaryHeader}>
              <h2 style={styles.summaryTitle}>Workflow Summary</h2>
              <button style={styles.summaryCloseBtn} onClick={() => setShowSummary(false)}>
                Close
              </button>
            </div>
            <div style={styles.summaryBody}>
              <h3 style={styles.summarySectionTitle}>A) Executive Summary</h3>
              <p>
                The workflow starts with HR creating and managing workforce structure, including hiring, employee
                onboarding, attendance, leave, and payroll. Once employees (especially PAMs) are onboarded, the
                operational flow begins with PAM login and daily task planning, which requires HPAM approval before
                execution.
              </p>
              <p>
                Post approval, PAM performs field activities such as survey, property identification, and data
                submission, which undergo HPAM validation and Admin approval before entering the CRM system. The CRM
                processes data and triggers presentation creation (CRE).
              </p>
              <p>
                The business flow follows a mandate-first approach, where Leasing Head/BDM collect requirements from
                brands before matching properties. Shortlisted properties move to visit scheduling and execution,
                followed by brand decision.
              </p>
              <p>
                If selected, the deal progresses through Legal → Finance → Technical execution, ending with store
                launch. The process includes multiple approval gates and loopbacks to ensure data accuracy, task
                validation, and payment completion.
              </p>

              <h3 style={styles.summarySectionTitle}>🧩 B) Role-wise Flow</h3>
              <ul style={styles.summaryList}>
                <li>
                  <strong>🔹 HR</strong>
                  <div>Create Job Opening → Manage Job Openings</div>
                  <div>Hiring → Create Employee → Manage Employees</div>
                  <div>Upload Documents</div>
                  <div>Attendance Management:</div>
                  <div>Office: Punch Machine</div>
                  <div>Field: Mobile Selfie + GPS</div>
                  <div>Leave Management</div>
                  <div>Payroll Generation</div>
                  <div>Handoff → PAM Login</div>
                </li>
                <li>
                  <strong>🔹 PAM</strong>
                  <div>Login → Selfie Attendance</div>
                  <div>Create Daily Task → Submit to HPAM</div>
                  <div>(After approval)</div>
                  <div>Area Selection → Street Mapping → Survey</div>
                  <div>Property Identification → Developer Connection</div>
                  <div>Data Upload → Report Submission</div>
                  <div>Conduct Site Visit (assigned by OM)</div>
                </li>
                <li>
                  <strong>🔹 HPAM</strong>
                  <div>Review Daily Task</div>
                  <div>Approve/Reject Task</div>
                  <div>Review PAM submitted data</div>
                  <div>Validate Data</div>
                  <div>Handoff → Admin</div>
                </li>
                <li>
                  <strong>🔹 Admin</strong>
                  <div>Review Data</div>
                  <div>Modify if required</div>
                  <div>Approve and push to CRM</div>
                </li>
                <li>
                  <strong>🔹 CRM</strong>
                  <div>Store Property Inventory</div>
                  <div>Manage Data</div>
                  <div>Trigger Presentation Creation</div>
                  <div>Match Properties (based on mandate)</div>
                </li>
                <li>
                  <strong>🔹 CRE (Presentation Team)</strong>
                  <div>Create PPT / Presentation</div>
                  <div>Upload Presentation</div>
                  <div>Share with Leasing Head & BDM</div>
                </li>
                <li>
                  <strong>🔹 Leasing Head / BDM</strong>
                  <div>Connect with Brands</div>
                  <div>Capture Brand Requirement</div>
                  <div>Initiate Mandate</div>
                </li>
                <li>
                  <strong>🔹 Brand</strong>
                  <div>Provide Requirement (Mandate)</div>
                  <div>Review Matched Properties</div>
                  <div>Shortlist Options</div>
                  <div>Request Visit</div>
                  <div>Final Decision (Select / Reject Property)</div>
                </li>
                <li>
                  <strong>🔹 OM (Operations Manager)</strong>
                  <div>Schedule Visit</div>
                  <div>Assign PAM</div>
                  <div>Manage logistics</div>
                </li>
                <li>
                  <strong>🔹 Legal Team</strong>
                  <div>Draft LOI</div>
                  <div>Negotiate Terms</div>
                  <div>Create Agreement</div>
                  <div>Finalize Legal Documents</div>
                </li>
                <li>
                  <strong>🔹 Finance Team</strong>
                  <div>Generate Invoice</div>
                  <div>Send Invoice</div>
                  <div>Track Payment</div>
                  <div>Confirm Payment</div>
                </li>
                <li>
                  <strong>🔹 Technical Manager</strong>
                  <div>Setup Property</div>
                  <div>Infrastructure Setup</div>
                  <div>Final Inspection</div>
                  <div>Launch Store</div>
                </li>
              </ul>

              <h3 style={styles.summarySectionTitle}>🔁 C) Decision & Loop Paths</h3>
              <ul style={styles.summaryList}>
                <li>
                  <strong>Task Approved? (HPAM)</strong>
                  <div>❌ No → Back to PAM (Create Daily Task)</div>
                  <div>✅ Yes → PAM starts field work</div>
                </li>
                <li>
                  <strong>Data Valid? (HPAM)</strong>
                  <div>❌ Invalid → Back to PAM (Area Selection / Rework)</div>
                  <div>✅ Valid → Forward to Admin</div>
                </li>
                <li>
                  <strong>Modify Required? (Admin)</strong>
                  <div>✅ Yes → Modify → Re-review</div>
                  <div>❌ No → Push to CRM</div>
                </li>
                <li>
                  <strong>Mandate Flow (Business)</strong>
                  <div>Requirement captured → Mandate created → CRM matches properties</div>
                </li>
                <li>
                  <strong>Property Selected? (Brand)</strong>
                  <div>❌ Rejected → Back to Business (re-sharing loop)</div>
                  <div>✅ Selected → Move to Legal</div>
                </li>
                <li>
                  <strong>Payment Done? (Finance)</strong>
                  <div>❌ No → Follow-up → Recheck</div>
                  <div>✅ Yes → Proceed to Technical</div>
                </li>
              </ul>

              <h3 style={styles.summarySectionTitle}>🧭 D) End-to-End Stage Timeline</h3>
              <ol style={styles.summaryList}>
                <li>HR Setup (Jobs → Employees → Attendance → Payroll)</li>
                <li>PAM Login & Attendance</li>
                <li>Daily Task Creation → HPAM Approval</li>
                <li>PAM Field Execution</li>
                <li>HPAM Data Validation</li>
                <li>Admin Review & CRM Entry</li>
                <li>CRM Processing → CRE Presentation</li>
                <li>Business Engagement (LH/BDM)</li>
                <li>Mandate Creation (Demand-first trigger)</li>
                <li>CRM Property Matching</li>
                <li>Brand Review & Shortlisting</li>
                <li>Visit Scheduling & Execution</li>
                <li>Brand Decision</li>
                <li>Legal Processing</li>
                <li>Finance Payment Cycle</li>
                <li>Technical Setup & Store Launch (END)</li>
              </ol>

              <h3 style={styles.summarySectionTitle}>⚠️ E) Risks / Bottlenecks (Top 5)</h3>
              <ul style={styles.summaryList}>
                <li>Task Approval Delay (HPAM) → Blocks entire field execution</li>
                <li>Poor Data Quality (PAM) → Causes repeated rework loops</li>
                <li>Admin Modification Cycle → Slows CRM entry</li>
                <li>Weak Mandate Clarity → Leads to poor property matching</li>
                <li>Payment Delays (Finance) → Blocks final execution</li>
              </ul>

              <h3 style={styles.summarySectionTitle}>📦 F) Compact UI Version (For Modal)</h3>
              <ul style={styles.summaryList}>
                <li>HR sets up jobs, employees, attendance, payroll → PAM login</li>
                <li>PAM creates daily task → HPAM approval required</li>
                <li>PAM executes field work → HPAM validates data</li>
                <li>Admin approves → CRM stores & processes data</li>
                <li>CRE creates presentation → sent to business team</li>
                <li>LH/BDM capture requirement → Mandate created (start of demand flow)</li>
                <li>CRM matches properties → Brand reviews, shortlists, requests visit</li>
                <li>Visit → Selection → Legal → Finance → Technical → Store Launch 🚀</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {showComments && (
        <div style={styles.summaryOverlay} onClick={() => setShowComments(false)}>
          <div style={styles.summaryModal} onClick={(event) => event.stopPropagation()}>
            <div style={styles.summaryHeader}>
              <h2 style={styles.summaryTitle}>Client Comments</h2>
              <button style={styles.summaryCloseBtn} onClick={() => setShowComments(false)}>
                Close
              </button>
            </div>
            <div style={styles.summaryBody}>
              <div style={styles.commentFormRow}>
                <input
                  style={styles.commentInput}
                  placeholder="Your name"
                  value={commentDraft.author}
                  onChange={(event) => setCommentDraft((prev) => ({ ...prev, author: event.target.value }))}
                />
              </div>
              <div style={styles.commentHintRow}>
                <span>Comment Context (Hovered Step): {hoverStep || "Hover a node to set context"}</span>
              </div>
              <textarea
                style={styles.commentTextarea}
                placeholder="Write your comment about this workflow..."
                value={commentDraft.message}
                onChange={(event) => setCommentDraft((prev) => ({ ...prev, message: event.target.value }))}
              />
              <div style={styles.commentActions}>
                <button
                  style={{
                    ...styles.summaryCloseBtn,
                    opacity: hoverStep ? 1 : 0.6,
                    cursor: hoverStep ? "pointer" : "not-allowed",
                  }}
                  onClick={addComment}
                  disabled={!hoverStep}
                >
                  Add Comment
                </button>
              </div>
              <div style={styles.commentList}>
                {comments.length === 0 ? (
                  <div style={styles.commentEmpty}>No comments yet.</div>
                ) : (
                  comments.map((comment) => (
                    <div key={comment.id} style={styles.commentCard}>
                      <div style={styles.commentMeta}>
                        <strong>{comment.author}</strong>
                        {comment.step && <span>{comment.step}</span>}
                      </div>
                      <div>{comment.message}</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  page: {
    height: "100vh",
    background:
      "radial-gradient(circle at 18% 12%, rgba(56,189,248,0.24) 0%, rgba(15,23,42,0.95) 36%), radial-gradient(circle at 84% 18%, rgba(99,102,241,0.22) 0%, rgba(2,6,23,0.92) 40%), linear-gradient(135deg, #030712 0%, #0b1225 45%, #111827 100%)",
    color: "#e2e8f0",
    fontFamily: "'Plus Jakarta Sans', 'SF Pro Display', 'Segoe UI', system-ui, sans-serif",
    overflow: "hidden",
    position: "relative",
  },
  backdropOrbOne: {
    position: "absolute",
    width: 380,
    height: 380,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(56,189,248,0.34) 0%, rgba(56,189,248,0) 70%)",
    top: -120,
    left: -80,
    filter: "blur(14px)",
    pointerEvents: "none",
  },
  backdropOrbTwo: {
    position: "absolute",
    width: 420,
    height: 420,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(129,140,248,0.26) 0%, rgba(129,140,248,0) 70%)",
    bottom: -170,
    right: -70,
    filter: "blur(18px)",
    pointerEvents: "none",
  },
  backdropNoise: {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    opacity: 0.26,
    backgroundImage:
      "radial-gradient(rgba(148,163,184,0.22) 0.5px, transparent 0.5px), radial-gradient(rgba(148,163,184,0.2) 0.5px, transparent 0.5px)",
    backgroundPosition: "0 0, 12px 12px",
    backgroundSize: "24px 24px",
  },
  header: {
    height: 74,
    padding: "12px 20px",
    borderBottom: "1px solid rgba(148, 163, 184, 0.24)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: "linear-gradient(120deg, rgba(15,23,42,0.62), rgba(15,23,42,0.38))",
    backdropFilter: "blur(14px)",
    boxShadow: "0 16px 34px rgba(2, 6, 23, 0.35)",
    position: "relative",
    zIndex: 2,
  },
  title: {
    margin: 0,
    fontSize: 20,
    fontWeight: 600,
    letterSpacing: 0.3,
  },
  subtitle: {
    margin: "2px 0 0",
    fontSize: 12,
    color: "#bfdbfe",
    letterSpacing: 0.3,
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  summaryBtn: {
    border: "1px solid rgba(148, 163, 184, 0.38)",
    borderRadius: 10,
    background: "linear-gradient(155deg, rgba(30,41,59,0.7), rgba(15,23,42,0.5))",
    color: "#e2e8f0",
    fontSize: 12,
    fontWeight: 600,
    padding: "8px 12px",
    cursor: "pointer",
    backdropFilter: "blur(10px)",
  },
  commentsBtn: {
    border: "1px solid rgba(148, 163, 184, 0.38)",
    borderRadius: 10,
    background: "linear-gradient(155deg, rgba(15,23,42,0.72), rgba(30,41,59,0.52))",
    color: "#e2e8f0",
    fontSize: 12,
    fontWeight: 600,
    padding: "8px 12px",
    cursor: "pointer",
    backdropFilter: "blur(10px)",
  },
  connection: (state) => ({
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.8,
    borderRadius: 999,
    padding: "5px 10px",
    background:
      state === "connected"
        ? "rgba(22, 163, 74, 0.2)"
        : state === "offline"
        ? "rgba(245, 158, 11, 0.22)"
        : "rgba(148, 163, 184, 0.18)",
    color: state === "connected" ? "#4ade80" : state === "offline" ? "#f59e0b" : "#94a3b8",
    border: "1px solid rgba(191, 219, 254, 0.26)",
    backdropFilter: "blur(10px)",
  }),
  btn: {
    width: 34,
    height: 34,
    border: "1px solid rgba(191, 219, 254, 0.28)",
    borderRadius: 10,
    background: "linear-gradient(160deg, rgba(37,99,235,0.75), rgba(59,130,246,0.5))",
    color: "#f8fafc",
    fontSize: 18,
    cursor: "pointer",
    backdropFilter: "blur(14px)",
    boxShadow: "0 10px 20px rgba(30, 64, 175, 0.34)",
  },
  layout: {
    height: "calc(100vh - 74px)",
    display: "block",
    position: "relative",
    zIndex: 1,
  },
  canvasWrap: {
    height: "100%",
    position: "relative",
    overflow: "hidden",
    background:
      "linear-gradient(120deg, rgba(15,23,42,0.46) 0%, rgba(15,23,42,0.22) 100%), repeating-linear-gradient(0deg, rgba(148,163,184,0.12) 0 1px, transparent 1px 30px), repeating-linear-gradient(90deg, rgba(148,163,184,0.12) 0 1px, transparent 1px 30px)",
    backdropFilter: "blur(8px)",
  },
  diagramCommentPinned: {
    position: "absolute",
    width: 240,
    zIndex: 30,
    pointerEvents: "none",
    border: "1px solid rgba(148,163,184,0.26)",
    borderRadius: 10,
    padding: "8px 10px",
    background: "linear-gradient(160deg, rgba(15,23,42,0.78), rgba(30,41,59,0.64))",
    color: "#e2e8f0",
    fontSize: 11,
    lineHeight: 1.35,
    boxShadow: "0 10px 20px rgba(2, 6, 23, 0.35)",
    backdropFilter: "blur(10px)",
  },
  diagramCommentMeta: {
    display: "flex",
    justifyContent: "space-between",
    gap: 8,
    marginBottom: 4,
    color: "#bfdbfe",
    fontSize: 10,
  },
  diagramCommentsEmpty: {
    position: "absolute",
    top: 14,
    right: 14,
    border: "1px solid rgba(148,163,184,0.24)",
    borderRadius: 10,
    padding: "8px 10px",
    background: "rgba(15,23,42,0.68)",
    color: "#94a3b8",
    fontSize: 11,
    backdropFilter: "blur(8px)",
  },
  canvas: {
    width: "100%",
    height: "100%",
    overflow: "hidden",
    cursor: "grab",
    touchAction: "none",
  },
  diagram: {
    transformOrigin: "0 0",
    transition: "transform 120ms ease-out",
    width: "max-content",
    padding: 28,
    borderRadius: 18,
    background: "linear-gradient(145deg, rgba(15,23,42,0.44), rgba(15,23,42,0.2))",
    border: "1px solid rgba(148,163,184,0.22)",
    boxShadow: "0 26px 46px rgba(2, 6, 23, 0.36)",
  },
  tooltip: {
    position: "fixed",
    maxWidth: 280,
    zIndex: 99,
    pointerEvents: "none",
    background: "linear-gradient(160deg, rgba(15,23,42,0.78), rgba(30,41,59,0.66))",
    border: "1px solid rgba(148,163,184,0.3)",
    borderRadius: 12,
    padding: "10px 12px",
    fontSize: 12,
    lineHeight: 1.4,
    boxShadow: "0 16px 30px rgba(2, 6, 23, 0.38)",
    backdropFilter: "blur(12px)",
  },
  summaryOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 120,
    background: "rgba(2, 6, 23, 0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  summaryModal: {
    width: "min(760px, 96vw)",
    maxHeight: "82vh",
    overflow: "auto",
    borderRadius: 16,
    border: "1px solid rgba(148,163,184,0.28)",
    background: "linear-gradient(165deg, rgba(15,23,42,0.88), rgba(30,41,59,0.76))",
    boxShadow: "0 24px 48px rgba(2, 6, 23, 0.45)",
    backdropFilter: "blur(16px)",
    color: "#e2e8f0",
  },
  summaryHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 16px",
    borderBottom: "1px solid rgba(148,163,184,0.2)",
  },
  summaryTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 700,
  },
  summaryCloseBtn: {
    border: "1px solid rgba(148,163,184,0.35)",
    borderRadius: 8,
    background: "rgba(15,23,42,0.64)",
    color: "#e2e8f0",
    padding: "6px 10px",
    cursor: "pointer",
  },
  summaryBody: {
    padding: "14px 16px 18px",
    fontSize: 13,
    lineHeight: 1.5,
  },
  summarySectionTitle: {
    margin: "14px 0 8px",
    fontSize: 14,
    color: "#cbd5e1",
    fontWeight: 700,
  },
  summaryList: {
    margin: "10px 0 0",
    paddingLeft: 20,
    display: "grid",
    gap: 8,
  },
  commentFormRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
    marginBottom: 8,
  },
  commentInput: {
    border: "1px solid rgba(148,163,184,0.35)",
    borderRadius: 8,
    background: "rgba(15,23,42,0.65)",
    color: "#e2e8f0",
    padding: "8px 10px",
    fontSize: 12,
  },
  commentTextarea: {
    width: "100%",
    minHeight: 84,
    border: "1px solid rgba(148,163,184,0.35)",
    borderRadius: 8,
    background: "rgba(15,23,42,0.65)",
    color: "#e2e8f0",
    padding: "10px",
    fontSize: 12,
    resize: "vertical",
    boxSizing: "border-box",
  },
  commentHintRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
    fontSize: 12,
    color: "#cbd5e1",
  },
  commentSmallBtn: {
    border: "1px solid rgba(148,163,184,0.35)",
    borderRadius: 8,
    background: "rgba(30,41,59,0.65)",
    color: "#e2e8f0",
    padding: "6px 8px",
    cursor: "pointer",
    fontSize: 11,
  },
  commentActions: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 10,
  },
  commentList: {
    marginTop: 12,
    display: "grid",
    gap: 8,
  },
  commentCard: {
    border: "1px solid rgba(148,163,184,0.25)",
    borderRadius: 10,
    padding: "10px 12px",
    background: "rgba(15,23,42,0.5)",
    fontSize: 12,
    lineHeight: 1.4,
  },
  commentMeta: {
    display: "flex",
    justifyContent: "space-between",
    gap: 8,
    marginBottom: 4,
    color: "#bfdbfe",
  },
  commentEmpty: {
    color: "#94a3b8",
    fontSize: 12,
  },
};

export default WorkflowDashboard;
